import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { RoutesInterface } from '@navigation/authRoutes';
import { ProfileTypes } from '@api/Users/User';
import { SIDEBAR_GROUP_ORDER, SIDEBAR_GROUPS, SidebarGroup } from '@navigation/sidebarGroups';
import { SidebarExternalLink } from '@navigation/externalLinks';
import { can } from '@utils/Permissions/Capabilities';

// ============================================================================
// Navegación del sidebar con grupos colapsables (2026-08-18).
//
// Nació con el cambio de "el admin ya no se cambia de perfil para trabajar":
// su menú pasó de 9 a ~15 renglones y una lista plana dejó de ser navegable.
// Los perfiles con pocas pantallas (gerente, colaborador, CEDIS) no declaran
// `group`, así que siguen viendo la MISMA lista plana de siempre.
//
// Qué se pinta:
//   - Rutas con `icon` (sin icon = pantalla de detalle, existe pero no se lista).
//   - Rutas cuyo `profiles` incluya el perfil activo (sin `profiles` = todos).
//   - Enlaces a otras apps (`links`) que el perfil activo `can`, después de las
//     rutas sueltas; abren pestaña nueva.
// El estado abierto/cerrado se recuerda POR DISPOSITIVO en localStorage.
// ============================================================================

const STORAGE_KEY = 'krispis.sidebarGroups';

/** NavLink que aplica la clase de MUI cuando la ruta está activa. */
const MyNavLink = React.forwardRef<any, any>(function MyNavLink(props, ref) {
    return (
        <NavLink
            ref={ref}
            to={props.to}
            className={({ isActive }) => `${props.className} ${isActive ? props.activeClassName : ''}`}
            onClick={props.onClick}
        >
            {props.children}
        </NavLink>
    );
});

const readOpenState = (): Partial<Record<SidebarGroup, boolean>> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        // localStorage lleno o deshabilitado: se opera con los defaults
        return {};
    }
};

interface SidebarNavProps {
    routes: RoutesInterface[];
    profile?: ProfileTypes;
    links?: SidebarExternalLink[];
    onNavigate: () => void;
}

const SidebarNav: React.FunctionComponent<SidebarNavProps> = ({ routes, profile, links = [], onNavigate }) => {
    const [openGroups, setOpenGroups] = useState<Partial<Record<SidebarGroup, boolean>>>(readOpenState);

    const isVisible = (route: RoutesInterface) =>
        !!route.icon && (!route.profiles || (!!profile && route.profiles.includes(profile)));

    const visibleRoutes = routes.filter(isVisible);
    const ungrouped = visibleRoutes.filter((route) => !route.group);
    const visibleLinks = links.filter((link) => can({ profile }, link.capability));

    const toggleGroup = (group: SidebarGroup) => {
        setOpenGroups((prev) => {
            const next = { ...prev, [group]: !(prev[group] ?? SIDEBAR_GROUPS[group].defaultOpen) };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            } catch {
                // Recordar el estado es una comodidad, no una función crítica
            }
            return next;
        });
    };

    const renderRoute = (route: RoutesInterface, key: React.Key, inset?: boolean) => {
        const Icon = route.icon as React.FunctionComponent;
        return (
            <ListItemButton
                key={key}
                component={MyNavLink}
                to={route.path}
                onClick={onNavigate}
                activeClassName="Mui-selected"
                sx={inset ? { pl: 3 } : undefined}
            >
                <ListItemIcon>
                    <Icon />
                </ListItemIcon>
                <ListItemText primary={route.name} />
            </ListItemButton>
        );
    };

    const renderLink = (link: SidebarExternalLink, key: React.Key) => {
        const Icon = link.icon;
        return (
            <ListItemButton
                key={key}
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onNavigate}
            >
                <ListItemIcon>
                    <Icon />
                </ListItemIcon>
                <ListItemText primary={link.name} />
                <OpenInNewIcon fontSize="small" color="disabled" />
            </ListItemButton>
        );
    };

    return (
        <List component="nav">
            {ungrouped.map((route, key) => renderRoute(route, `top-${key}`))}
            {visibleLinks.map((link, key) => renderLink(link, `link-${key}`))}

            {SIDEBAR_GROUP_ORDER.map((group) => {
                const groupRoutes = visibleRoutes.filter((route) => route.group === group);
                if (groupRoutes.length === 0) {
                    return null;
                }
                const isOpen = openGroups[group] ?? SIDEBAR_GROUPS[group].defaultOpen;
                return (
                    <React.Fragment key={group}>
                        <ListItemButton onClick={() => toggleGroup(group)}>
                            <ListItemText
                                primary={
                                    <Typography variant="overline" fontWeight={700} color="text.secondary">
                                        {SIDEBAR_GROUPS[group].label}
                                    </Typography>
                                }
                            />
                            {isOpen ? <ExpandLess color="disabled" /> : <ExpandMore color="disabled" />}
                        </ListItemButton>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {groupRoutes.map((route, key) => renderRoute(route, `${group}-${key}`, true))}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            })}
        </List>
    );
};

export default SidebarNav;
